import React, { Fragment, useEffect, useRef, useState } from "react";
import { Menu, Transition } from "@headlessui/react";

import { Button } from "components/Buttons/Button";
import { Checkbox } from "components/Checkbox/Checkbox";
import { icons } from "constants/icons";

import { FILTER_KEYS, FilterParams } from "types/common";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { useClickOutside } from "../../hooks/useClickOutside";
import { jointIdList } from "../../utils/formatters";


type Props = {
  title?: string;
  filterKey: FILTER_KEYS;
  filterParams: FilterParams[];
  onApply: (filterKey: FILTER_KEYS, filterParams: FilterParams[]) => void;
  onReset: (filterKey: FILTER_KEYS, filterParams: FilterParams[]) => void;
};

export const FilterDropdown = ({
  title,
  filterKey,
  filterParams,
  onApply,
  onReset,
}: Props) => {
  const [state, setState] = useState<FilterParams[]>([]);
  const ref: any = useRef();

  useClickOutside(ref, () => setState(filterParams));

  useEffect(() => {
    setState(filterParams);
  }, [filterParams]);

  const onItemClick = (id: string) => {
    const params = [...state].map(({ ...param }) => {
      if (param.id === id) {
        param.checked = !param.checked;
      }
      return param;
    });
    setState(params);
  };

  const handleReset = () => {
    const params = [...state].map((param) => ({ ...param, checked: false }));
    setState(params);
    onReset(filterKey, params);
  };

  const handleApply = () => {
    onApply(filterKey, state);
  };
  return (
    <Menu as="div" className=" inline-block text-left">
      <div>
        <Menu.Button className="inline-flex justify-center w-full px-4 py-2">
          <img src={icons.filterTable} alt="filterTable" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="flex flex-col absolute left-0 w-56 mt-2 origin-top-right bg-white px-5 pt-4 pb-8 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[1]">
          <div className="flex flex-col" ref={ref}>
            <div className="px-1 py-1 mb-2">
              <h4 className="text-bgDark font-normal">{title}</h4>
            </div>

            {state.map((filterParam) => {
              const handleClick = () => {
                onItemClick(filterParam.id);
              };
              return (
                <button
                  type="button"
                  key={filterParam.id}
                  className="px-1 py-1 cursor-pointer"
                  onClick={handleClick}
                >
                  <Checkbox
                    onChange={handleClick}
                    label={jointIdList[filterParam.id]}
                    checked={filterParam.checked}
                  />
                </button>
              );
            })}

            <div className="w-full flex items-center justify-between border-t border-border mt-8 py-1">
              <Menu.Button>
                <Button
                  title="Reset"
                  onClick={handleReset}
                  type={ButtonType.text}
                  classNames="text-grayMedium font-normal ml-[-13px]"
                />
              </Menu.Button>
              <Menu.Button>
                <Button
                  title="OK"
                  onClick={handleApply}
                  type={ButtonType.small}
                  colorScheme={ButtonColorScheme.gradient}
                  classNames="py-2"
                />
              </Menu.Button>
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
